import { secp256k1 } from '@noble/curves/secp256k1.js';
import { hmac } from '@noble/hashes/hmac.js';
import { sha512 } from '@noble/hashes/sha512.js';
import { ethers } from 'ethers';

// Same BIP32 public-key-only child derivation as src/services/btcXpub.js (both
// ETH/BSC/Polygon and BTC use secp256k1), duplicated here rather than shared
// since the two modules encode addresses completely differently.
export function isValidHex(str) {
  return typeof str === 'string' && str.length > 0 && str.length % 2 === 0 && /^[0-9a-fA-F]+$/.test(str);
}

function ser32(i) {
  const b = Buffer.alloc(4);
  b.writeUInt32BE(i, 0);
  return b;
}

function ckdPub(pubkeyHex, chainCodeHex, index) {
  if (index >= 0x80000000) throw new Error('Cannot derive a hardened child from a public key');
  const chainCode = Buffer.from(chainCodeHex, 'hex');
  const point = secp256k1.ProjectivePoint.fromHex(pubkeyHex);
  const compressed = Buffer.from(point.toRawBytes(true));
  const data = Buffer.concat([compressed, ser32(index)]);
  const I = hmac(sha512, chainCode, data);
  const IL = I.subarray(0, 32);
  const IR = I.subarray(32, 64);
  const ilPoint = secp256k1.ProjectivePoint.BASE.multiply(BigInt('0x' + Buffer.from(IL).toString('hex')));
  const childPoint = ilPoint.add(point);
  return {
    pubkeyHex: Buffer.from(childPoint.toRawBytes(true)).toString('hex'),
    chainCodeHex: Buffer.from(IR).toString('hex'),
  };
}

// Derives the checksummed 0x address at .../0/<addressIndex> from an account
// node's {publicKey, chainCode} captured at m/44'/60'/0'/0 (the "change" level).
// ETH/BSC/Polygon all share this same path and curve, so one cached node covers all three.
export function deriveEvmAddress(accountPubkeyHex, accountChainCodeHex, addressIndex) {
  const child = ckdPub(accountPubkeyHex, accountChainCodeHex, addressIndex);
  return ethers.utils.computeAddress('0x' + child.pubkeyHex);
}
